import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { Button } from './ui/button';
import { Camera, X, Loader2 } from 'lucide-react';
import { SafeImage } from './SafeImage';
import { resolveFileUrl } from '../lib/fileUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;

/**
 * Equipment photo uploader.
 *
 * Compresses to a 300–400 KB target before POSTing to
 * /api/equipment/:id/photos. Enforces the 5-photo cap on the client AND
 * the server. Photos render as compact thumbnails with a remove button.
 *
 * The `photos` prop may contain a mix of legacy bare strings (pre-migration
 * uploads) and the new object shape — both render via the same helper.
 */
function asUrl(p) {
  if (!p) return '';
  if (typeof p === 'string') return p;
  return p.url || '';
}

export default function EquipmentPhotoUploader({ equipmentId, photos = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos per equipment item.`);
      return;
    }

    setUploading(true);
    try {
      // Compress to ~350 KB target. maxWidthOrHeight 1280 keeps the photo
      // readable for ID-plate / damage shots without bloating storage.
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.4,
        maxWidthOrHeight: 1280,
        useWebWorker: true,
        initialQuality: 0.8,
      });

      const fd = new FormData();
      fd.append('file', compressed, file.name);

      const res = await axios.post(
        `${API}/equipment/${equipmentId}/photos`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      onChange(res.data.photos || []);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (idx) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      const res = await axios.delete(`${API}/equipment/${equipmentId}/photos/${idx}`);
      onChange(res.data.photos || []);
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    }
  };

  return (
    <div data-testid="equipment-photo-uploader">
      <div className="flex flex-wrap gap-2 mb-2">
        {photos.map((p, i) => {
          const url = asUrl(p);
          if (!url) return null;
          return (
            <div
              key={i}
              className="relative w-20 h-20 rounded border border-slate-200 overflow-hidden group bg-slate-50"
              data-testid={`equipment-photo-${i}`}
            >
              <SafeImage src={resolveFileUrl(url, 'preview')} alt={`Photo ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove photo"
                data-testid={`equipment-photo-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        {photos.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-slate-400 hover:border-amber-400 hover:text-amber-600 transition-colors disabled:opacity-50"
            data-testid="equipment-photo-add"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Camera className="w-5 h-5 mb-1" />
                <span className="text-[10px]">Add photo</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="hidden"
        onChange={handleSelect}
      />
      <p className="text-xs text-slate-500">
        {photos.length} of {MAX_PHOTOS} photos used. Compressed to ~350 KB on upload.
      </p>
    </div>
  );
}
